import { Pin, Link2, ArrowRightLeft, MessageSquare } from "lucide-react";
import { colourStyle } from "@/data/notebookOptions";
import { NoteReactions } from "./NoteReactions";

const fmt = (iso) => { try { return new Date(iso).toLocaleDateString(undefined, { day: "numeric", month: "short" }); } catch { return "—"; } };

// Sticky-note / post-it card. Colour drives the background; comments & mentions
// intentionally live in the detail view to keep the card clean and scannable.
export const NoteCard = ({ note, canWrite, myName, onOpen, onTogglePin, onReact, onDragStart, index = 0 }) => {
  const c = colourStyle(note.colour);
  const linked = (note.related_records?.length || 0) + (note.converted_to?.length || 0);
  const tilt = note.pinned ? 0 : (index % 3 === 0 ? -1.1 : index % 3 === 1 ? 0.8 : -0.4);
  return (
    <div
      data-testid={`note-card-${note.id}`}
      role="button"
      tabIndex={0}
      draggable={canWrite}
      onDragStart={(e) => { if (canWrite && onDragStart) onDragStart(e, note); }}
      aria-label={`Open note: ${note.title}`}
      onClick={() => onOpen(note)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); onOpen(note); } }}
      style={{ backgroundColor: c.bg, borderColor: c.border, transform: `rotate(${tilt}deg)` }}
      className="group relative cursor-pointer rounded-xl border p-3 shadow-[0_3px_10px_rgba(20,36,95,0.10)] transition-all hover:rotate-0 hover:-translate-y-0.5 hover:shadow-[0_10px_22px_rgba(20,36,95,0.16)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#14245F]/30"
    >
      <div className="mb-1 flex items-start justify-between gap-2">
        <span className="rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: c.accent, backgroundColor: "rgba(255,255,255,0.55)" }}>{note.category}</span>
        {canWrite && (
          <button
            data-testid={`note-pin-${note.id}`}
            onClick={(e) => { e.stopPropagation(); onTogglePin(note); }}
            className={`-mr-1 -mt-1 rounded-full p-1 transition-colors ${note.pinned ? "text-[#B8860B]" : "text-black/25 hover:text-[#B8860B]"}`}
            title={note.pinned ? "Unpin" : "Pin"}
          >
            <Pin size={15} fill={note.pinned ? "#F5B301" : "none"} />
          </button>
        )}
      </div>

      <h4 className="font-heading text-sm font-bold leading-snug text-[#1A1A1A]">{note.title}</h4>
      {(note.converted_to?.length || 0) > 0 && (
        <span data-testid={`note-converted-badge-${note.id}`} className="mt-1 inline-flex items-center gap-1 rounded-full bg-white/60 px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide" style={{ color: c.accent }}>
          <ArrowRightLeft size={10} /> Converted to {note.converted_to[note.converted_to.length - 1].target}
        </span>
      )}
      {note.body && <p className="mt-1 line-clamp-3 text-[13px] leading-snug text-[#42474D]">{note.body.replace(/[#*>`\-]/g, "")}</p>}

      <div className="mt-2.5 flex items-center justify-between gap-2 text-[11px]" style={{ color: c.accent }}>
        <span className="truncate font-semibold">{note.author} · {fmt(note.created_at)}</span>
        <div className="flex shrink-0 items-center gap-2 opacity-80">
          {linked > 0 && <span className="inline-flex items-center gap-0.5" title="Linked records"><Link2 size={12} /> {linked}</span>}
          {(note.converted_to?.length || 0) > 0 && <ArrowRightLeft size={12} title="Converted" />}
          {(note.comments?.length || 0) > 0 && <span className="inline-flex items-center gap-0.5" title="Notes in detail"><MessageSquare size={12} /> {note.comments.length}</span>}
        </div>
      </div>

      <div className="mt-2 border-t border-black/5 pt-2">
        <NoteReactions note={note} canReact={canWrite && (myName === "Steven" || myName === "Richard")} myName={myName} onReact={onReact} compact />
      </div>
    </div>
  );
};
